import React, { useState, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Tabs,
  Tab,
  LinearProgress,
  Chip,
  Badge,
  ListItemText
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { ref, update, push, set, get } from 'firebase/database';
import { database } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useUser } from '../contexts/UserContext';
import { useEvents } from '../contexts/EventsContext';
import { useSchools } from '../contexts/SchoolsContext';
import { usePrograms } from '../contexts/ProgramsContext';
import { Autocomplete } from '@mui/material';
import { findSchoolIdByName } from '../utils/schoolUtils';

const AddEditEvents: React.FC = () => {
  const { events, loading, error, refresh } = useEvents();
  const { schools } = useSchools();
  const { user } = useUser();
  const { programs } = usePrograms();
  const [selectedHost, setSelectedHost] = useState<string>('');
  const [filterMode, setFilterMode] = useState<'future' | 'all'>('future');
  
  // Tab state
  const [currentTab, setCurrentTab] = useState(0);
  
  // AI functionality state
  const [aiSearchInProgress, setAiSearchInProgress] = useState(false);
  const [searchProgress, setSearchProgress] = useState<any>(null);
  const [searchId, setSearchId] = useState<string | null>(null);
  const [searchSummary, setSearchSummary] = useState<any>(null);
  const [aiGeneratedEvents, setAiGeneratedEvents] = useState<any[]>([]);
  const [schoolsWithDrafts, setSchoolsWithDrafts] = useState<any[]>([]);
  const [selectedDraftSchool, setSelectedDraftSchool] = useState<string>('');
  const [aiEventsLoading, setAiEventsLoading] = useState(false);
  
  // Status filter state - default excludes rejected events
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(['pending', 'approved', 'published']);
  
  // Draft AI Events tab state
  const [selectedDraftTabSchool, setSelectedDraftTabSchool] = useState<string>('');
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<any>(null);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    location: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    website: '',
    registerRequired: false,
    eventType: 'physical',
    private: false,
    hostName: '',
    eventCategory: ['General'] as string[]
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Get program categories that have at least one program
  const availableProgramCategories = useMemo(() => {
    const categoryCounts: { [category: string]: number } = {};
    
    // Count programs per category
    programs.forEach(program => {
      if (program.category && program.category.trim() !== '') {
        categoryCounts[program.category] = (categoryCounts[program.category] || 0) + 1;
      }
    });
    
    // Filter categories that have at least one program and sort alphabetically
    return Object.keys(categoryCounts)
      .filter(category => categoryCounts[category] > 0)
      .sort();
  }, [programs]);

  // Additional event category options
  const additionalEventCategories = [
    'Admissions, Applications, Offers',
    'Clubs',
    'Financial Aid',
    'General',
    'Residence/Meal Plans',
    'Scholarships'
  ];

  // Combine program categories with additional categories and sort alphabetically
  const allEventCategories = useMemo(() => {
    const combined = [...availableProgramCategories, ...additionalEventCategories];
    return combined.sort();
  }, [availableProgramCategories]);

  // Filter events based on filter mode (future vs all)
  const filteredEventsByMode = useMemo(() => {
    if (filterMode === 'all') {
      return events;
    }
    
    // Filter for future events only
    const now = new Date();
    return events.filter(event => {
      if (!event.startTime) return false;
      
      try {
        const eventDate = new Date(event.startTime);
        return eventDate > now;
      } catch {
        return false;
      }
    });
  }, [events, filterMode]);

  // Get unique host names and sort them alphabetically
  const uniqueHosts = useMemo(() => {
    const hosts = filteredEventsByMode
      .map(event => event.hostName || event.host || '')
      .filter(host => host.trim() !== '')
      .filter((host, index, array) => array.indexOf(host) === index) // Remove duplicates
      .sort((a, b) => a.localeCompare(b));
    
    return hosts;
  }, [filteredEventsByMode]);

  // Get all school names from Canadian schools, sorted alphabetically
  const allSchoolNames = useMemo(() => {
    const schoolNames = schools.map(school => school.name);
    
    // Remove duplicates and sort alphabetically
    const uniqueNames = Array.from(new Set(schoolNames));
    return uniqueNames.sort((a, b) => a.localeCompare(b));
  }, [schools]);

  // Set default host when hosts are loaded
  React.useEffect(() => {
    if (uniqueHosts.length > 0 && !selectedHost) {
      setSelectedHost(uniqueHosts[0]);
    }
  }, [uniqueHosts, selectedHost]);

  // Filter events by selected host and sort by date
  const filteredEvents = useMemo(() => {
    if (!selectedHost) return [];
    
    return filteredEventsByMode
      .filter(event => (event.hostName || event.host || '') === selectedHost)
      .sort((a, b) => {
        const dateA = new Date(a.startTime || '');
        const dateB = new Date(b.startTime || '');
        return dateA.getTime() - dateB.getTime(); // Ascending order
      });
  }, [filteredEventsByMode, selectedHost]);

  // Filter events for draft school (used in Draft AI Events tab)
  const filteredEventsForDraftSchool = useMemo(() => {
    if (!selectedDraftSchool) return [];
    
    // Get the school name from the selected draft school
    const selectedSchool = schoolsWithDrafts.find(s => s.id === selectedDraftSchool);
    if (!selectedSchool) return [];
    
    return filteredEventsByMode
      .filter(event => {
        // Filter by school name (case-insensitive)
        // Check if the event's host matches the selected school name
        const eventHost = (event.hostName || event.host || '').toLowerCase();
        const selectedSchoolName = selectedSchool.name.toLowerCase();
        return eventHost === selectedSchoolName;
      })
      .sort((a, b) => {
        const dateA = new Date(a.startTime || '');
        const dateB = new Date(b.startTime || '');
        return dateA.getTime() - dateB.getTime(); // Ascending order
      });
  }, [filteredEventsByMode, selectedDraftSchool, schoolsWithDrafts]);

  // Filter AI events by selected statuses
  const filteredAiEvents = useMemo(() => {
    return aiGeneratedEvents.filter(aiEvent => {
      const status = aiEvent.draftStatus || 'pending';
      // Map status values to filter options
      const statusMap: { [key: string]: string } = {
        'pending': 'pending',
        'approved': 'approved', 
        'rejected': 'rejected',
        '': 'pending' // Default to pending if no status
      };
      
      const mappedStatus = statusMap[status] || 'pending';
      return selectedStatuses.includes(mappedStatus);
    });
  }, [aiGeneratedEvents, selectedStatuses]);

  // Get Canadian schools for the Draft AI Events tab dropdown
  const canadianSchools = useMemo(() => {
    if (!schools || schools.length === 0) {
      return [];
    }
    
    // All schools in the main 'schools' table are Canadian
    // US schools are in a separate 'schools_US' table
    const filtered = schools
      .filter(school => {
        // Filter for schools that have websites
        const hasWebsite = school.website && school.website.trim() !== '';
        return hasWebsite;
      })
      .map(school => ({
        id: school.id,
        name: school.name,
        website: school.website,
        city: school.city,
        province: school.province
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    
    return filtered;
  }, [schools]);

  // Format date for display
  const formatDate = (dateTimeString: string) => {
    if (!dateTimeString) return '';
    
    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return dateTimeString; // Return original if invalid
      
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateTimeString;
    }
  };

  // Format time for display
  const formatTime = (dateTimeString: string) => {
    if (!dateTimeString) return '';
    
    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return '';
      
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return '';
    }
  };

  // Format date and time in m/d/yyyy (h:mma/p) format
  const formatDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return '';
    
    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return dateTimeString;
      
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const year = date.getFullYear();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, '0');
      
      return `${month}/${day}/${year} (${displayHours}:${displayMinutes}${ampm})`;
    } catch {
      return dateTimeString;
    }
  };

  const handleHostChange = (event: any) => {
    setSelectedHost(event.target.value);
  };

  const handleFilterModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newFilterMode: 'future' | 'all' | null,
  ) => {
    if (newFilterMode !== null) {
      setFilterMode(newFilterMode);
      // Reset selected host when filter mode changes
      setSelectedHost('');
    }
  };

  // Parse date and time from datetime string
  const parseDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return { date: '', time: '' };
    
    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return { date: '', time: '' };
      
      const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD format
      const timeStr = date.toTimeString().slice(0, 5); // HH:MM format
      
      return { date: dateStr, time: timeStr };
    } catch {
      return { date: '', time: '' };
    }
  };

  // Handle opening edit modal
  const handleEditClick = (event: any) => {
    setEditingEvent(event);
    setIsCreateMode(false);
    
    // Check if this is an AI draft event (has eventContent array)
    let eventData = event;
    if (event.eventContent && event.eventContent.length > 0) {
      // This is an AI draft event, use the first event content
      eventData = event.eventContent[0];
    }
    
    // Parse start and end date/time
    const startDateTime = parseDateTime(eventData.startTime || '');
    const endDateTime = parseDateTime(eventData.endTime || '');
    
    setEditForm({
      name: eventData.title || eventData.name || '',
      description: eventData.description || '',
      location: eventData.address || eventData.location || '',
      startDate: startDateTime.date,
      startTime: startDateTime.time,
      endDate: endDateTime.date,
      endTime: endDateTime.time,
      website: eventData.website || '',
      registerRequired: eventData.registerRequired || false,
      eventType: eventData.eventType || 'physical',
      private: eventData.private || false,
      hostName: eventData.hostName || eventData.host || event.schoolName || '',
    eventCategory: Array.isArray(eventData.eventCategory) 
      ? eventData.eventCategory 
      : eventData.eventCategory 
        ? [eventData.eventCategory] 
        : ['General']
    });
    
    setEditModalOpen(true);
    setSaveError(null);
  };

  // Handle opening create modal
  const handleCreateClick = () => {
    setEditingEvent(null);
    setIsCreateMode(true);
    
    setEditForm({
      name: '',
      description: '',
      location: '',
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',
      website: '',
      registerRequired: false,
      eventType: 'physical',
      private: false,
      hostName: selectedHost || '',
      eventCategory: ['General']
    });
    
    setEditModalOpen(true);
    setSaveError(null);
  };

  // Handle edit form changes
  const handleEditFormChange = (field: string, value: string | boolean | string[]) => {
    setEditForm(prev => {
      const updatedData = {
        ...prev,
        [field]: value
      };
      
      // Auto-set End Date to Start Date when Start Date is selected (only if End Date is empty)
      if (field === 'startDate' && typeof value === 'string' && value) {
        // Only auto-populate if the start date is a complete date (MM/DD/YYYY format)
        const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
        if (dateRegex.test(value) && !prev.endDate) {
          updatedData.endDate = value;
        }
      }
      
      // Auto-set End Time to Start Time + 1 hour when Start Time is selected (only if End Time is empty)
      if (field === 'startTime' && typeof value === 'string' && value && !prev.endTime) {
        // Parse the time value (could be in HH:MM AM/PM format or HH:MM format)
        let timeValue = value.trim();
        let hours = 0;
        let minutes = 0;
        
        // Handle AM/PM format
        if (timeValue.includes('AM') || timeValue.includes('PM')) {
          const isPM = timeValue.includes('PM');
          const timePart = timeValue.replace(/\s*(AM|PM)/i, '').trim();
          const [hourStr, minuteStr] = timePart.split(':');
          hours = parseInt(hourStr, 10);
          minutes = parseInt(minuteStr || '0', 10);
          
          // Convert to 24-hour format
          if (isPM && hours !== 12) {
            hours += 12;
          } else if (!isPM && hours === 12) {
            hours = 0;
          }
        } else {
          // Handle 24-hour format
          const [hourStr, minuteStr] = timeValue.split(':');
          hours = parseInt(hourStr, 10);
          minutes = parseInt(minuteStr || '0', 10);
        }
        
        // Add 1 hour
        hours += 1;
        if (hours >= 24) {
          hours = 0; // Wrap to next day
        }
        
        // Format as HH:MM
        updatedData.endTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      }
      
      // Auto-populate Physical Location with "Virtual" when Event Type is set to Virtual Event
      if (field === 'eventType' && value === 'virtual') {
        updatedData.location = 'Virtual';
      }
      
      // Clear location when switching to non-physical/non-hybrid event types
      if (field === 'eventType' && value !== 'physical' && value !== 'hybrid' && value !== 'virtual') {
        updatedData.location = '';
      }
      
      return updatedData;
    });
  };

  // Handle saving changes
  const handleSave = async () => {
    if (!user?.uid) {
      setSaveError('User authentication required');
      return;
    }

    console.log('Saving event with categories:', editForm.eventCategory);
    setIsSaving(true);
    setSaveError(null);

    try {
      // Combine date and time fields
      const startDateTime = editForm.startDate && editForm.startTime 
        ? `${editForm.startDate} ${editForm.startTime}` 
        : '';
      const endDateTime = editForm.endDate && editForm.endTime
        ? `${editForm.endDate} ${editForm.endTime}`
        : '';

      // Validate website is required for hybrid events
      if (editForm.eventType === 'hybrid' && !editForm.website.trim()) {
        throw new Error('Website is required for Hybrid Events');
      }

      const eventContent = {
        title: editForm.name,
        description: editForm.description,
        address: editForm.location,
        startTime: startDateTime,
        endTime: endDateTime,
        website: editForm.website,
        registerRequired: editForm.registerRequired,
        eventType: editForm.eventType,
        private: editForm.private,
        hostName: editForm.hostName,
        eventCategory: editForm.eventCategory.length > 0 ? editForm.eventCategory : ['General']
      };

      if (isCreateMode) {
        // Create new event
        // Get the host school ID from the host name
        if (!editForm.hostName || editForm.hostName.trim() === '') {
          throw new Error('Host name is required');
        }

        const schoolId = await findSchoolIdByName(editForm.hostName);
        if (!schoolId) {
          throw new Error(`School not found: ${editForm.hostName}`);
        }

        const eventData = {
          eventContent,
          postTime: Date.now(),
        };

        // Create the event in Firebase
        const eventsRef = ref(database, `schoolEvent/${schoolId}/${user.uid}`);
        const newEventRef = push(eventsRef);
        
        await set(newEventRef, eventData);
      } else {
        // Update existing event
        if (!editingEvent) {
          setSaveError('Event information is missing');
          return;
        }

        // Check if this is an AI draft event
        if (editingEvent.eventContent && editingEvent.eventContent.length > 0) {
          // This is an AI draft event - approve it with the updated content
          const schoolId = editingEvent.schoolId || selectedDraftSchool;
          if (!schoolId) {
            setSaveError('School ID is missing for AI draft event');
            return;
          }

          console.log('Calling approveAIGeneratedEvent with:', {
            schoolId: schoolId,
            eventId: editingEvent.id,
            eventData: eventContent
          });
          
          await approveAIGeneratedEvent({
            schoolId: schoolId,
            eventId: editingEvent.id,
            eventData: eventContent
          });
        } else {
          // This is a regular published event
          if (!editingEvent.schoolId || !editingEvent.userId) {
            setSaveError('Event information is missing');
            return;
          }

          const eventRef = ref(database, `schoolEvent/${editingEvent.schoolId}/${editingEvent.userId}/${editingEvent.id}/eventContent`);
          await update(eventRef, eventContent);
        }
      }

      // Refresh events data
      refresh();
      
      // If this was an AI draft event, also refresh the AI events
      if (editingEvent && editingEvent.eventContent && editingEvent.eventContent.length > 0) {
        if (selectedDraftSchool) {
          loadAIGeneratedEvents(selectedDraftSchool);
        }
        loadSchoolsWithDrafts();
      }
      
      // Close modal
      setEditModalOpen(false);
      setEditingEvent(null);
      setIsCreateMode(false);
    } catch (error) {
      console.error('Error saving event:', error);
      setSaveError('Failed to save event. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle canceling edit
  const handleCancel = () => {
    setEditModalOpen(false);
    setEditingEvent(null);
    setIsCreateMode(false);
    setSaveError(null);
  };

  // Firebase Functions setup
  const functions = getFunctions();
  const findUpcomingEvents = httpsCallable(functions, 'findUpcomingEventsForAllSchools');
  const getBulkSearchProgress = httpsCallable(functions, 'getBulkSearchProgress');
  const getAIGeneratedEvents = httpsCallable(functions, 'getAIGeneratedEvents');
  const approveAIGeneratedEvent = httpsCallable(functions, 'approveAIGeneratedEvent');
  const rejectAIGeneratedEvent = httpsCallable(functions, 'rejectAIGeneratedEvent');
  const getSchoolsWithDraftEvents = httpsCallable(functions, 'getSchoolsWithDraftEvents');

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    if (newValue === 1) {
      // When switching to Draft AI Events tab, load schools with drafts
      loadSchoolsWithDrafts();
    }
  };

  // Load schools that have draft AI events
  const loadSchoolsWithDrafts = async () => {
    try {
      setAiEventsLoading(true);
      const result = await getSchoolsWithDraftEvents();
      setSchoolsWithDrafts((result.data as any).schools || []);
    } catch (error) {
      console.error('Error loading schools with drafts:', error);
      setSchoolsWithDrafts([]);
    } finally {
      setAiEventsLoading(false);
    }
  };

  // Start bulk AI search
  const handleFindNewEvents = async () => {
    try {
      setAiSearchInProgress(true);
      setSearchProgress(null);
      
      // Pass the selected school name if one is selected
      const searchParams = selectedDraftTabSchool ? { schoolName: selectedDraftTabSchool } : {};
      
      console.log('Starting AI search with params:', searchParams);
      console.log('Selected draft tab school:', selectedDraftTabSchool);
      
      const result = await findUpcomingEvents(searchParams);
      console.log('AI search result:', result);
      
      const searchId = (result.data as any).searchId;
      setSearchId(searchId);
      
      // Start polling for progress
      pollSearchProgress(searchId);
      
    } catch (error) {
      console.error('Error starting AI search:', error);
      setAiSearchInProgress(false);
    }
  };

  // Poll search progress
  const pollSearchProgress = async (searchId: string) => {
    try {
      const result = await getBulkSearchProgress({ searchId });
      const progress = (result.data as any).progress;
      setSearchProgress(progress);
      
      if (progress.status === 'running') {
        // Continue polling
        setTimeout(() => pollSearchProgress(searchId), 2000);
      } else if (progress.status === 'completed') {
        setAiSearchInProgress(false);
        // Store search summary for display
        setSearchSummary(progress);
        // Reload schools with drafts
        loadSchoolsWithDrafts();
      } else if (progress.status === 'failed') {
        setAiSearchInProgress(false);
        console.error('AI search failed:', progress.error);
      }
    } catch (error) {
      console.error('Error polling search progress:', error);
      setAiSearchInProgress(false);
    }
  };

  // Load AI generated events for selected school
  const loadAIGeneratedEvents = async (schoolId: string) => {
    try {
      setAiEventsLoading(true);
      const result = await getAIGeneratedEvents({ schoolId });
      setAiGeneratedEvents((result.data as any).events || []);
    } catch (error) {
      console.error('Error loading AI generated events:', error);
      setAiGeneratedEvents([]);
    } finally {
      setAiEventsLoading(false);
    }
  };

  // Handle school selection for drafts
  const handleDraftSchoolChange = (schoolId: string) => {
    setSelectedDraftSchool(schoolId);
    if (schoolId) {
      loadAIGeneratedEvents(schoolId);
    } else {
      setAiGeneratedEvents([]);
    }
  };

  // Handle status filter change
  const handleStatusFilterChange = (event: any) => {
    const value = event.target.value;
    setSelectedStatuses(typeof value === 'string' ? value.split(',') : value);
  };

  // Handle rejecting an AI draft event
  const handleRejectAIDraftEvent = async (eventId: string, schoolId: string) => {
    try {
      await rejectAIGeneratedEvent({
        schoolId: schoolId,
        eventId: eventId,
        reason: 'Rejected by user review'
      });
      
      // Refresh the AI events for the current school
      if (selectedDraftSchool) {
        loadAIGeneratedEvents(selectedDraftSchool);
      }
      
      // Refresh schools with drafts
      loadSchoolsWithDrafts();
      
      console.log('AI draft event rejected successfully');
    } catch (error) {
      console.error('Error rejecting AI draft event:', error);
    }
  };

  // Handle school selection in Draft AI Events tab
  const handleDraftTabSchoolChange = (schoolName: string) => {
    setSelectedDraftTabSchool(schoolName);
  };

  if (loading) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 2, mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom>
            Add / Edit Events
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        </Paper>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 2, mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom>
            Add / Edit Events
          </Typography>
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 2, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">
            Add / Edit Events
          </Typography>
          {currentTab === 0 && (
            <ToggleButtonGroup
              value={filterMode}
              exclusive
              onChange={handleFilterModeChange}
              aria-label="filter mode"
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  border: '1px solid #e0e0e0',
                  color: '#666',
                  backgroundColor: '#fff',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#1976d2',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: '#1565c0',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="future" aria-label="future events">
                Future Events
              </ToggleButton>
              <ToggleButton value="all" aria-label="show all">
                Show All
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={currentTab} onChange={handleTabChange} aria-label="events tabs">
            <Tab label="Add/Edit Events" />
            <Tab 
              label={
                <Badge badgeContent={schoolsWithDrafts.length} color="primary">
                  Draft AI Events
                </Badge>
              } 
            />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {currentTab === 0 && (
          <Box>
            {/* Original Add/Edit Events content */}
        
        {/* Host Selection Dropdown */}
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel id="host-select-label">Select Host / School:</InputLabel>
            <Select
              labelId="host-select-label"
              id="host-select"
              value={selectedHost}
              label="Select Host / School:"
              onChange={handleHostChange}
            >
              {uniqueHosts.map((host) => (
                <MenuItem key={host} value={host}>
                  {host}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Events Table */}
        {selectedHost && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Events for {selectedHost}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateClick}
                sx={{ 
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  },
                }}
              >
                + Create a New Event
              </Button>
            </Box>
            
            {filteredEvents.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Event Title</strong></TableCell>
                      <TableCell><strong>Location</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredEvents.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell>
                          {formatDate(event.startTime || '')} {formatTime(event.startTime || '')}
                        </TableCell>
                        <TableCell>{event.title || event.name || ''}</TableCell>
                        <TableCell>{event.address || event.location || ''}</TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() => handleEditClick(event)}
                            color="primary"
                            size="small"
                            title="Edit Event"
                          >
                            <EditIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                No events found for {selectedHost}.
              </Typography>
            )}
          </Box>
        )}
        </Box>
      )}

      {/* Edit Event Modal */}
      <Dialog 
        open={editModalOpen} 
        onClose={handleCancel}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{isCreateMode ? 'Create New Event' : 'Edit Event'}</DialogTitle>
        <DialogContent>
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Event Title"
                value={editForm.name}
                onChange={(e) => handleEditFormChange('name', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={8}>
              <Autocomplete
                freeSolo
                options={allSchoolNames}
                value={editForm.hostName}
                onChange={(event, newValue) => {
                  handleEditFormChange('hostName', newValue || '');
                }}
                onInputChange={(event, newInputValue) => {
                  handleEditFormChange('hostName', newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Host Name"
                    placeholder="Select a school or enter custom host name"
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Autocomplete
                multiple
                options={allEventCategories}
                value={editForm.eventCategory}
                onChange={(event, newValue) => {
                  handleEditFormChange('eventCategory', newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Event Categories"
                    placeholder="Select categories (optional)"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                      key={option}
                    />
                  ))
                }
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    {option}
                  </Box>
                )}
                isOptionEqualToValue={(option, value) => option === value}
                noOptionsText="No categories available"
                freeSolo={false}
                sx={{ width: '100%' }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={editForm.description}
                onChange={(e) => handleEditFormChange('description', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={editForm.startDate}
                onChange={(e) => handleEditFormChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Time"
                type="time"
                value={editForm.startTime}
                onChange={(e) => handleEditFormChange('startTime', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={editForm.endDate}
                onChange={(e) => handleEditFormChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Time"
                type="time"
                value={editForm.endTime}
                onChange={(e) => handleEditFormChange('endTime', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={editForm.eventType}
                  label="Event Type"
                  onChange={(e) => handleEditFormChange('eventType', e.target.value)}
                >
                  <MenuItem value="physical">Physical Event</MenuItem>
                  <MenuItem value="virtual">Virtual Event</MenuItem>
                  <MenuItem value="hybrid">Hybrid Event</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Location - shown for Physical Event, Virtual Event, and Hybrid Event */}
            {(editForm.eventType === 'physical' || editForm.eventType === 'virtual' || editForm.eventType === 'hybrid') && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Location"
                  value={editForm.location}
                  onChange={(e) => handleEditFormChange('location', e.target.value)}
                  required={editForm.eventType === 'physical' || editForm.eventType === 'hybrid'}
                />
              </Grid>
            )}
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={editForm.eventType === 'hybrid' ? 'Website' : 'Website (optional)'}
                value={editForm.website}
                onChange={(e) => handleEditFormChange('website', e.target.value)}
                placeholder="https://example.com"
                required={editForm.eventType === 'hybrid'}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editForm.registerRequired}
                    onChange={(e) => handleEditFormChange('registerRequired', e.target.checked)}
                  />
                }
                label="Registration Required"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editForm.private}
                    onChange={(e) => handleEditFormChange('private', e.target.checked)}
                  />
                }
                label="Private Event"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={isSaving}
          >
            {isSaving ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Draft AI Events Tab */}
      {currentTab === 1 && (
        <Box>
          {/* School Selection for AI Search */}
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="draft-tab-school-select-label">Select School / Host:</InputLabel>
                    <Select
                      labelId="draft-tab-school-select-label"
                      id="draft-tab-school-select"
                      value={selectedDraftTabSchool}
                      label="Select School / Host:"
                      onChange={(e) => handleDraftTabSchoolChange(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>All Canadian Schools (Random 3)</em>
                      </MenuItem>
                      <MenuItem value="ALL_SCHOOLS">
                        <em>All Schools (Production Mode)</em>
                      </MenuItem>
                {canadianSchools.map((school) => (
                  <MenuItem key={school.id} value={school.name}>
                    <Box>
                      <Typography variant="body1">
                        {school.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {school.city}, {school.province}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Find New Events Button */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleFindNewEvents}
              disabled={aiSearchInProgress}
              sx={{
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
                minWidth: 200,
              }}
            >
              {aiSearchInProgress ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Searching...
                </>
              ) : (
                'Find New Events'
              )}
            </Button>
          </Box>

          {/* Search Progress */}
          {searchProgress && searchProgress.status === 'running' && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Search Progress
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ mr: 2 }}>
                  {searchProgress.currentSchool || 'Starting search...'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ({searchProgress.schoolsProcessed}/{searchProgress.totalSchools} schools)
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={(searchProgress.schoolsProcessed / searchProgress.totalSchools) * 100}
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                Events found: {searchProgress.eventsFound}
              </Typography>
            </Box>
          )}

          {/* Search Summary */}
          {searchSummary && searchSummary.status === 'completed' && (
            <Box sx={{ mb: 3, p: 2, backgroundColor: '#e8f5e8', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom color="success.main">
                ✅ Search Completed Successfully!
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2">
                    <strong>Total Events Found:</strong> {searchSummary.eventsFound}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Schools Processed:</strong> {searchSummary.schoolsProcessed || searchSummary.totalSchools || 0}/{searchSummary.totalSchools || 0}
                  </Typography>
                </Grid>
              </Grid>
              
              {/* Selected Schools */}
              {searchSummary.selectedSchools && searchSummary.selectedSchools.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Schools Selected for Testing:
                  </Typography>
                  {searchSummary.selectedSchools.map((school: any, index: number) => (
                    <Chip
                      key={index}
                      label={`${school.name} (${school.city}, ${school.province})`}
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                      color={school.type === 'canadian' ? 'primary' : 'secondary'}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* School Selection for Drafts */}
          {schoolsWithDrafts.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <InputLabel id="draft-school-select-label">Select School with Draft Events:</InputLabel>
                <Select
                  labelId="draft-school-select-label"
                  id="draft-school-select"
                  value={selectedDraftSchool}
                  label="Select School with Draft Events:"
                  onChange={(e) => handleDraftSchoolChange(e.target.value)}
                >
                  <MenuItem value="">
                    <em>All Schools</em>
                  </MenuItem>
                  {schoolsWithDrafts.map((school) => (
                    <MenuItem key={school.id} value={school.id}>
                      <Box>
                        <Typography variant="body1">
                          {school.name} ({school.draftCount} drafts)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {school.city}, {school.province}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Status Filter */}
          {selectedDraftSchool && (
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <InputLabel id="status-filter-label">Filter by Status:</InputLabel>
                <Select
                  labelId="status-filter-label"
                  id="status-filter"
                  multiple
                  value={selectedStatuses}
                  onChange={handleStatusFilterChange}
                  label="Filter by Status:"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip 
                          key={value} 
                          label={value.charAt(0).toUpperCase() + value.slice(1)} 
                          size="small" 
                          color={value === 'approved' ? 'success' : value === 'rejected' ? 'error' : 'warning'}
                        />
                      ))}
                    </Box>
                  )}
                >
                  <MenuItem value="pending">
                    <Checkbox checked={selectedStatuses.indexOf('pending') > -1} />
                    <ListItemText primary="Pending" />
                  </MenuItem>
                  <MenuItem value="approved">
                    <Checkbox checked={selectedStatuses.indexOf('approved') > -1} />
                    <ListItemText primary="Approved" />
                  </MenuItem>
                  <MenuItem value="rejected">
                    <Checkbox checked={selectedStatuses.indexOf('rejected') > -1} />
                    <ListItemText primary="Rejected" />
                  </MenuItem>
                  <MenuItem value="published">
                    <Checkbox checked={selectedStatuses.indexOf('published') > -1} />
                    <ListItemText primary="Published" />
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Combined Events Display */}
          {selectedDraftSchool && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Events for {schoolsWithDrafts.find(s => s.id === selectedDraftSchool)?.name}
              </Typography>
              
              {aiEventsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Type</strong></TableCell>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell><strong>Event Title</strong></TableCell>
                        <TableCell><strong>Location</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* Published Events for Selected Draft School */}
                      {filteredEventsForDraftSchool.map((event) => (
                        <TableRow key={event.id}>
                          <TableCell>
                            <Chip label="Published" color="success" size="small" />
                          </TableCell>
                          <TableCell>
                            {formatDate(event.startTime || '')} {formatTime(event.startTime || '')}
                          </TableCell>
                          <TableCell>{event.title || event.name || ''}</TableCell>
                          <TableCell>{event.address || event.location || ''}</TableCell>
                          <TableCell>
                            <Chip label="Active" color="success" size="small" />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() => handleEditClick(event)}
                              color="primary"
                              size="small"
                              title="Edit Event"
                            >
                              <EditIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      
                      {/* AI Generated Draft Events */}
                      {filteredAiEvents.map((aiEvent) => (
                        <TableRow key={aiEvent.id} sx={{ backgroundColor: '#fff3e0' }}>
                          <TableCell>
                            <Box>
                              <Chip label="AI Draft" color="warning" size="small" />
                              <Typography variant="caption" display="block" color="text.secondary">
                                {aiEvent.schoolType === 'canadian' ? '🇨🇦' : '🇺🇸'} {aiEvent.schoolName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {aiEvent.eventContent && aiEvent.eventContent.length > 0 && 
                              formatDateTime(aiEvent.eventContent[0].startTime || '')}
                          </TableCell>
                          <TableCell>
                            {aiEvent.eventContent && aiEvent.eventContent.length > 0 && 
                              aiEvent.eventContent[0].title}
                          </TableCell>
                          <TableCell>
                            {aiEvent.eventContent && aiEvent.eventContent.length > 0 && 
                              aiEvent.eventContent[0].location}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={aiEvent.draftStatus || 'Pending'} 
                              color={aiEvent.draftStatus === 'approved' ? 'success' : 'warning'} 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                onClick={() => handleEditClick(aiEvent)}
                                color="primary"
                                size="small"
                                title="Review AI Event"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                onClick={() => handleRejectAIDraftEvent(aiEvent.id, aiEvent.schoolId)}
                                color="error"
                                size="small"
                                title="Reject AI Event"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Empty State */}
          {schoolsWithDrafts.length === 0 && !aiSearchInProgress && (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Draft AI Events Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click "Find New Events" to search all schools for upcoming events using AI.
              </Typography>
            </Box>
          )}
        </Box>
      )}
      </Paper>
    </Box>
  );
};

export default AddEditEvents;
